import { AlertColor } from '@mui/material';

/**
 * Human-readable presentation of a single validation flag. The backend emits
 * machine codes like `UNIT_CASING_NORMALISED`; the review UI shows reviewers a
 * title + explanation in an alert instead.
 */
export interface ValidationFlagDescriptor {
  /** Short, human-readable title (e.g. "Unit casing normalised"). */
  title: string;
  /** One-sentence explanation of what the flag means for the reviewer. */
  description: string;
  /** MUI alert severity — drives the colour/icon (error/warning/info). */
  severity: AlertColor;
}

/**
 * Lookup from a validation-flag code to its reviewer-facing copy. Mirrors the
 * flags raised across the honey pipeline (extractor, normaliser, validator).
 * `info` = benign/audit-only, `warning` = confirm this, `error` = value/verdict
 * could not be trusted.
 */
const FLAG_DESCRIPTORS: Record<string, ValidationFlagDescriptor> = {
  // ── Extraction (generic-Excel / structured-Excel) ──────────────────────────
  SENTINEL_REF: {
    title: 'Spreadsheet error in source',
    description:
      'The source cell held a spreadsheet error (e.g. #REF! or #DIV/0!), so no value was captured.',
    severity: 'error'
  },
  LLM_CELL_MISMATCH: {
    title: 'Cell value mismatch',
    description:
      "The model's reported value disagreed with the actual cell. The cell value was kept and flagged for review.",
    severity: 'error'
  },
  PARTIAL_SHEET: {
    title: 'Partial sheet read',
    description: 'The sheet was too large to read in full, so some rows may be missing.',
    severity: 'warning'
  },
  VALUE_UNANCHORED: {
    title: 'Value not found in the document text',
    description:
      "The AI quoted a source sentence for this value, but that quote could not be found in the PDF's text — the value may be misread. Please check it against the source page.",
    severity: 'warning'
  },
  SCANNED_PDF: {
    title: 'Scanned document',
    description:
      'The source PDF has no machine-readable text (a scan or image-only export), so values could not be verified against it. Review against the source pages.',
    severity: 'warning'
  },
  DUPLICATE_SUPPRESSED: {
    title: 'Restated elsewhere in this file',
    description:
      'This datapoint also appears elsewhere in the same file (a restated summary). Kept for audit; it is not double-counted.',
    severity: 'info'
  },
  CROSS_FILE_DUPLICATE: {
    title: 'Already reported in another file',
    description:
      "Another file already carries this metric for the same entity and period, with the same value. Open this file's review view (filter to the file) to resolve all of its duplicates in one decision — or keep it deliberately, if it is a genuine repeat.",
    severity: 'warning'
  },
  CROSS_FILE_CONFLICT: {
    title: 'Disagrees with another file',
    description:
      'Another file reports this metric for the same entity and period with a DIFFERENT value. Open this file\'s review view (filter to the file) and choose "new and updated records" to replace the earlier value — or decide per record which figure is correct.',
    severity: 'warning'
  },
  SUSPECTED_TOTAL: {
    title: 'Looks like a subtotal',
    description:
      'This value exactly equals the sum of the rows above it, so it may be a subtotal rather than a datapoint. Approving it alongside those rows would double-count — please check the source.',
    severity: 'warning'
  },
  RECONCILIATION_MISMATCH: {
    title: "Total doesn't add up",
    description:
      'This total does not equal the sum of its breakdown in the source sheet. The source data may contain an arithmetic error — please check before approving.',
    severity: 'warning'
  },
  // ── Mapping (learning loop) ─────────────────────────────────────────────────
  MAPPED_FROM_ALIAS: {
    title: 'Mapped by a learned rule',
    description:
      'A reviewer previously confirmed this exact label for this client, so it mapped automatically — no AI guess involved. Retire or change the rule under Learned rules if it is wrong.',
    severity: 'info'
  },
  MAPPED_WITH_HINT: {
    title: 'Matched a metric you named',
    description:
      'You listed this metric in the upload context, and the file independently matched it — two signals agreeing, so it is treated as an exact match rather than a suggestion.',
    severity: 'info'
  },
  // ── Normalisation (units / precision) ──────────────────────────────────────
  UNIT_CASING_NORMALISED: {
    title: 'Unit casing normalised',
    description:
      "The unit's casing or spacing was standardised (e.g. KWH → kWh). The value itself is unchanged.",
    severity: 'info'
  },
  UNIT_CONVERTED: {
    title: 'Unit converted',
    description: "The value was converted into the metric's canonical unit (e.g. MWh → kWh).",
    severity: 'info'
  },
  PRECISION_TRUNCATED: {
    title: 'Precision truncated',
    description: 'A long decimal was rounded for storage. Review if exact precision matters here.',
    severity: 'info'
  },
  INTENSITY_UNIT: {
    title: 'Intensity / ratio unit',
    description:
      'The unit looks like a rate or ratio (e.g. ZAR/hour). Audit-only — confirm an intensity is intended.',
    severity: 'info'
  },
  UNIT_MISMATCH: {
    title: 'Unit mismatch',
    description:
      "The extracted unit does not match the metric's expected unit; the value may need conversion.",
    severity: 'warning'
  },
  UNIT_UNRECOGNISED: {
    title: 'Unrecognised unit',
    description: 'The extracted unit was not recognised, so it could not be normalised.',
    severity: 'warning'
  },
  NO_CONVERSION: {
    title: 'No unit conversion',
    description:
      "No conversion exists from the extracted unit to the metric's canonical unit; the value was left as-is.",
    severity: 'warning'
  },
  UNIT_ASSUMED_CANONICAL: {
    title: 'No unit in the source',
    description:
      "The source cell held a bare number with no unit written beside it, so the value was taken to already be in the metric's canonical unit. Nothing was converted.",
    severity: 'info'
  },
  // ── Validation (value / range / sign / qualitative) ────────────────────────
  NEGATIVE_VALUE: {
    title: 'Negative value',
    description:
      'The value is negative. Expected for credits or refunds, unusual for most metrics — please confirm.',
    severity: 'warning'
  },
  OUT_OF_RANGE: {
    title: 'Out of range',
    description:
      "The value falls outside the metric's plausible range (e.g. a percentage above 100).",
    severity: 'warning'
  },
  NOT_A_NUMBER: {
    title: "Value isn't a number",
    description: 'The cell value could not be read as a number, so no normalised value was stored.',
    severity: 'error'
  },
  VALUE_NOT_NUMERIC: {
    title: "Value isn't a number",
    description: 'The cell value could not be read as a number, so no normalised value was stored.',
    severity: 'error'
  },
  QUALITATIVE_HAS_NUMERIC_VALUE: {
    title: 'Numeric value on a Yes/No metric',
    description:
      'A qualitative (Yes/No/Partial) metric carries a numeric value — confirm which is correct.',
    severity: 'warning'
  },
  QUALITATIVE_UNPARSEABLE: {
    title: 'Unreadable Yes/No verdict',
    description: 'The compliance text could not be read as Yes, No or Partial.',
    severity: 'error'
  }
};

/** Title-cases a raw flag code (`SOME_FLAG` → `Some flag`) for the fallback descriptor. */
function humaniseCode(code: string): string {
  const words = code.toLowerCase().replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * True when the record's metric was resolved from a learned rule (the per-client
 * alias loop) rather than the AI — the `MAPPED_FROM_ALIAS` provenance flag. Split
 * on `:` like {@link describeValidationFlag} so an eventual detail suffix cannot
 * break the match.
 * @param {Array<string> | null} flags The record's `validationFlags`.
 * @returns {boolean}
 */
export function isMappedFromLearnedRule(flags: Array<string> | null): boolean {
  return flags?.some((flag) => flag.split(':')[0] === 'MAPPED_FROM_ALIAS') ?? false;
}

/**
 * The record's cross-file collision kind, if any: `'duplicate'` (same value already
 * reported in another file) or `'conflict'` (a DIFFERENT value reported elsewhere —
 * the stronger warning, so it wins when both flags are present). Null when the
 * record collides with nothing. Drives the row-level chip so a duplicated row never
 * reads as plain green "Exact 100%" (Maxine R5 ③). Prefix-matched on `:` like
 * {@link describeValidationFlag}.
 * @param {Array<string> | null} flags The record's `validationFlags`.
 * @returns {'duplicate' | 'conflict' | null}
 */
export function crossFileFlag(flags: Array<string> | null): 'duplicate' | 'conflict' | null {
  const codes = (flags ?? []).map((flag) => flag.split(':')[0]);
  if (codes.includes('CROSS_FILE_CONFLICT')) return 'conflict';
  if (codes.includes('CROSS_FILE_DUPLICATE')) return 'duplicate';
  return null;
}

/**
 * Resolve a raw validation flag to its reviewer-facing descriptor.
 *
 * Flags may carry a `:detail` suffix (e.g. `UNIT_MISMATCH:expected kWh`); the
 * code before the colon selects the descriptor and the detail is appended to the
 * description. Unknown codes fall back to a humanised title so a newly-added
 * backend flag still renders sensibly rather than breaking the UI.
 * @param {string} flag The raw flag string from `record.validationFlags`.
 * @returns {ValidationFlagDescriptor}
 */
export function describeValidationFlag(flag: string): ValidationFlagDescriptor {
  const [code, ...detailParts] = flag.split(':');
  const detail = detailParts.join(':').trim();
  const base: ValidationFlagDescriptor = FLAG_DESCRIPTORS[code] ?? {
    title: humaniseCode(code),
    description: 'Flagged by the validator for review.',
    severity: 'warning'
  };
  return detail === '' ? base : { ...base, description: `${base.description} (${detail})` };
}
